<template>
  <base-modal
    :ref="modal.key"
    :show="open"
    maxWidth="800"
    :title="modal.title"
    @on-confirm="saveItem"
    @on-cancel="hide()"
    :okText="$t('Save')"
    :closeText="$t('Close')"
  >
    <v-text-field
      v-model="model.description"
      :label="$t('Description')"
      :error-messages="descriptionErrors"
      @input="$v.model.description.$touch()"
      @blur="$v.model.description.$touch()"
    />
    <v-row>
      <v-col>
        <v-row>
          <v-col cols="8">
            <BaseDatePicker
              v-model="model.startDate"
              :label="$t('StartDate')"
              :error-messages="startDateErrors"
              @input="$v.model.startDate.$touch()"
              @blur="$v.model.startDate.$touch()"
              v-if="open"
            />
          </v-col>
          <v-col cols="4">
            <BaseTimePicker
              v-model="model.startTime"
              :label="$t('Hour')"
              :error-messages="startTimeErrors"
              @input="$v.model.startTime.$touch()"
              @blur="$v.model.startTime.$touch()"
              v-if="open"
            />
          </v-col>
        </v-row>
      </v-col>

      <v-col>
        <v-row>
          <v-col cols="8">
            <BaseDatePicker
              v-model="model.endDate"
              :label="$t('EndDate')"
              @change="setTime"
              clearable
              :error-messages="endDateErrors"
              @input="$v.model.endDate.$touch()"
              @blur="$v.model.endDate.$touch()"
              v-if="open"
            />
          </v-col>
          <v-col cols="4">
            <BaseTimePicker
              v-model="model.endTime"
              :label="$t('Hour')"
              clearable
              :error-messages="endTimeErrors"
              @input="$v.model.endTime.$touch()"
              @blur="$v.model.endTime.$touch()"
              v-if="open"
            />
          </v-col>
        </v-row>
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <ColorPicker
          :label="$t('TextColor')"
          v-model="model.textColor"
          :error-messages="textColorErrors"
          @input="$v.model.textColor.$touch()"
          @blur="$v.model.textColor.$touch()"
        />
      </v-col>
      <v-col>
        <ColorPicker
          :label="$t('BackgroundColor')"
          v-model="model.backgroundColor"
          :error-messages="backgroundColorErrors"
          @input="$v.model.backgroundColor.$touch()"
          @blur="$v.model.backgroundColor.$touch()"
        />
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <v-select
          :label="$t('Target')"
          v-model="model.target"
          :items="targetList"
          @change="loadValues()"
          item-text="text"
          item-value="id"
          :error-messages="targetErrors"
          @input="$v.model.target.$touch()"
          @blur="$v.model.target.$touch()"
        ></v-select>
      </v-col>
      <v-col v-if="model.target > 1">
        <v-select
          :label="$t('Value')"
          v-model="model.targetValue"
          :items="targetValueList"
          item-text="text"
          item-value="id"
          multiple
          :error-messages="targetValueErrors"
          @input="$v.model.targetValue.$touch()"
          @blur="$v.model.targetValue.$touch()"
        ></v-select>
      </v-col>
    </v-row>
    <VueEditor v-model="model.message" />
  </base-modal>
</template>

<script>
import { VueEditor } from "vue2-editor";
import { required, requiredIf } from "vuelidate/lib/validators";
import { validationMixin } from "vuelidate";
import ColorPicker from "@/components/widgets/ColorPicker";
import Utils from "@/ecm/common/Utils";
import Settings from "@/ecm/models/widget/newsfeed/NewsfeedSettings";
import RoleService from "@/services/RoleService";
import UserService from "@/services/UserService";

export default {
  components: {
    VueEditor,
    ColorPicker,
  },
  data() {
    return {
      targetList: [
        {
          id: "1",
          text: this.$t("Organization"),
        },
        {
          id: "2",
          text: this.$t("Role"),
        },
        {
          id: "3",
          text: this.$t("User"),
        },
      ],
      targetValueList: [],
      model: new Settings(),
      modal: {
        title: this.$t("Newsfeed"),
        key: "newsfeedModal",
      },
      open: false,
    };
  },
  mixins: [validationMixin],
  validations: {
    model: {
      description: { required },
      backgroundColor: { required },
      textColor: { required },
      target: { required },
      startDate: { required },
      startTime: { required },
      endDate: {
        required: requiredIf(function () {
          return this.model.endTime;
        }),
      },
      endTime: {
        required: requiredIf(function () {
          return this.model.endDate;
        }),
      },
      target: { required },
      targetValue: {
        required: requiredIf(function () {
          return this.model.target != "1";
        }),
      },
    },
  },
  computed: {
    modalInstance() {
      return this.$refs[this.modal.key];
    },
    descriptionErrors() {
      const errors = [];
      if (!this.$v.model.description.$dirty) return errors;
      !this.$v.model.description.required &&
        errors.push(this.$t("RequiredField"));
      return errors;
    },
    backgroundColorErrors() {
      const errors = [];
      if (!this.$v.model.backgroundColor.$dirty) return errors;
      !this.$v.model.backgroundColor.required &&
        errors.push(this.$t("RequiredField"));
      return errors;
    },
    textColorErrors() {
      const errors = [];
      if (!this.$v.model.textColor.$dirty) return errors;
      !this.$v.model.textColor.required &&
        errors.push(this.$t("RequiredField"));
      return errors;
    },
    targetErrors() {
      const errors = [];
      if (!this.$v.model.target.$dirty) return errors;
      !this.$v.model.target.required && errors.push(this.$t("RequiredField"));
      return errors;
    },
    startDateErrors() {
      const errors = [];
      if (!this.$v.model.startDate.$dirty) return errors;
      !this.$v.model.startDate.required &&
        errors.push(this.$t("RequiredField"));
      return errors;
    },
    startTimeErrors() {
      const errors = [];
      if (!this.$v.model.startTime.$dirty) return errors;
      !this.$v.model.startTime.required &&
        errors.push(this.$t("RequiredField"));
      return errors;
    },
    endDateErrors() {
      const errors = [];
      if (!this.$v.model.endDate.$dirty) return errors;
      !this.$v.model.endDate.required && errors.push(this.$t("RequiredField"));
      return errors;
    },
    endTimeErrors() {
      const errors = [];
      if (!this.$v.model.endTime.$dirty) return errors;
      !this.$v.model.endTime.required && errors.push(this.$t("RequiredField"));
      return errors;
    },
    targetErrors() {
      const errors = [];
      if (!this.$v.model.target.$dirty) return errors;
      !this.$v.model.target.required && errors.push(this.$t("RequiredField"));
      return errors;
    },
    targetValueErrors() {
      const errors = [];
      if (!this.$v.model.targetValue.$dirty) return errors;
      !this.$v.model.targetValue.required &&
        errors.push(this.$t("RequiredField"));
      return errors;
    },
  },
  methods: {
    setTime() {
      this.model.endTime = "23:59";
    },
    loadValues(targeValue) {
      this.targetValueList = [];
      this.model.targetValue = "";
      if (this.model.target == "2") {
        RoleService.getList(true).then((result) => {
          this.targetValueList = result;
          if (targeValue) this.model.targetValue = targeValue;
        });
      }
      if (this.model.target == "3") {
        UserService.getList(true).then((result) => {
          this.targetValueList = result;
          if (targeValue) this.model.targetValue = targeValue;
        });
      }
    },
    invalidDates() {
      const userFormat = `${this.$store.getters["account/user"].MetricDateFormat} HH:mm`;

      const startDate = Utils.date.fromUserFormat(
        this.model.startDate + " " + this.model.startTime,
        userFormat
      );
      const endDate = Utils.date.fromUserFormat(
        this.model.endDate + " " + this.model.endTime,
        userFormat
      );

      return startDate > endDate;
    },
    saveItem() {
      this.$v.$touch();

      if (this.$v.$invalid) {
        return;
      }

      if (this.invalidDates()) {
        Utils.toast.error(this.$t("StartDateBeforeEndDateException"));
        return;
      }

      this.modalInstance.progress(true);
      this.$emit("on-item-saved", Utils.object.toJS(this.model));
      this.modalInstance.progress(false);
      this.hide();
    },
    new() {
      this.$v.$reset();
      this.model = new Settings();
      this.show();
    },
    edit(model) {
      this.model = model;
      this.loadValues(model.targetValue);
      this.show();
    },
    show() {
      this.open = true;
    },
    hide() {
      this.open = false;
    },
  },
};
</script>
